import { useRef } from 'react'
import { useIntl } from 'react-intl'

import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import useToggle from 'hooks/useToggle'

interface Props {
  value?: 'total' | 'perPerson'
  onChange?: (value: 'total' | 'perPerson') => void
}

/**
 * Chip + dropdown for switching between "Total price" and "Per person" on hotel/charter search
 * surfaces. Visually matches the offer-page price-mode chip (see `PriceModeSelectMobile`) so the
 * two surfaces feel consistent — both surfaces previously used different styles (segmented vs
 * dropdown) which forced the search header to wrap onto a second row on mobile.
 */
function HotelPriceToggle({ value = 'total', onChange }: Props) {
  const intl = useIntl()
  const { value: isOpen, toggle, off: close } = useToggle()
  const anchorRef = useRef<HTMLButtonElement>(null)

  const totalPriceLabel = intl.formatMessage({
    id: 'kGgDSvtOWXZi9MJV-MQC',
    defaultMessage: 'Total price',
    description: 'Toggle label for showing total price in hotel price toggle',
  })

  const perPersonLabel = intl.formatMessage({
    id: 'nrkVKqo7sbL3FTKdBcdQ',
    defaultMessage: 'Per person',
    description: 'Toggle label for showing per person price in hotel price toggle',
  })

  const selectedLabel = value === 'perPerson' ? perPersonLabel : totalPriceLabel

  const handleSelect = (mode: 'total' | 'perPerson') => {
    onChange?.(mode)
    close()
  }

  return <>
    <FilterChip size="medium" endIcon={<LineAngleDownIcon />} onClick={toggle} ref={anchorRef}>
      {selectedLabel}
    </FilterChip>
    <DropdownList size="S" open={isOpen} onClose={close} anchorRef={anchorRef} triggerRef={anchorRef} placement="bottom-end">
      <ClickableListItem title={totalPriceLabel} onClick={() => handleSelect('total')} />
      <ClickableListItem title={perPersonLabel} onClick={() => handleSelect('perPerson')} />
    </DropdownList>
  </>
}

export default HotelPriceToggle
